import React, { useContext, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { CREATE_ROOMS } from '../../../../../apollo/mutations';
import ErrorMessages from '../../../../ui/message/ErrorMessage';
import { messages as updateMessages, rules as updateRules } from '../../../../../validations/room/add';
import { buildNetworkErrorMessages, buildValidationErrorMessages } from '../../../../../validations/common';
import { extractGraphQLErrors, extractNetworkErrors } from '../../../../../utils/apolloErrorHelpers';
import { localStateReducer, scrollTo } from '../../../../../utils/common';
import { ModalDispatchContext } from '../../../../ui/modal/ModalContext';
// eslint-disable-next-line
import Input from '../../../../ui/form/Input';
import OneButtonModal from '../../../../ui/modal/standard/OneButtonModal';
// eslint-disable-next-line
import { Icon, CloseButton, PrimaryButton, YellowButton } from '@cb/apricot-react';
import { validate, validateAll } from 'indicative';
import { Room, RoomWId } from '../types';

import './BulkAddRooms.scss';
import '../../../common/common.scss';

import { useNavigate } from 'react-router-dom';
import RosterV3 from '../../../../common/roster/RosterV3';
import AddRoomRow from './AddRoomRow';
import { title } from 'process';
import uniqueId from 'lodash/uniqueId';

export interface BulkAddRoomsProps {
  initialRows: number;
  onClose: () => void;
  handleRoomChanges: (currentRooms: { title: string; capacity: string }[]) => void;
  handleDrawerDirty: (isDirty: boolean) => void;
  rooms: { title: string; capacity: string }[];
  footerButtonLabel: string;
  type?: 'create' | 'import';
  enableResetBtn: boolean;
}

function BulkAddRooms({
  initialRows = 1,
  onClose = () => {},
  // eslint-disable-next-line
  handleDrawerDirty = (isDirty) => {},
  rooms = [],
  // eslint-disable-next-line
  handleRoomChanges = (currentRooms) => {},
  footerButtonLabel,
  type = 'create',
  enableResetBtn,
}: BulkAddRoomsProps) {
  const navigate = useNavigate();
  function deepCopy(obj = {}) {
    return JSON.parse(JSON.stringify(obj));
  }

  const headers = [{ title: '' }, { title: 'Room Name', sortField: [] }, { title: 'Capacity', sortField: [] }];

  function handleAddRow(e) {
    e && e.preventDefault && e.preventDefault();

    // Deep copy form objects.
    const form = deepCopy(localState.form);
    const formErrors = deepCopy(localState.formErrors);
    const formErrorsByRoomId = { ...localState.formErrorsByRoomId };

    const emptyRoom = { ...formFields, id: uniqueId() };
    form.push(emptyRoom);

    formErrors.push({ title: emptyRoom.title, capacity: emptyRoom.capacity });
    formErrorsByRoomId[emptyRoom.id] = { title: emptyRoom.title, capacity: emptyRoom.capacity };

    handleRoomChanges(form);
    setLocalState({
      form,
      formErrorsByRoomId,
      formErrors,
      pageOpts: { pageNum: -1 },
    });
  }

  function handleDelete(room) {
    let capacityTotal = 0;
    let roomTotal = 0;

    // Deep copy form objects.
    const form = deepCopy(localState.form);
    const formErrors = deepCopy(localState.formErrors);
    const formErrorsByRoomId = { ...localState.formErrorsByRoomId };
    const roomIdIndex = form.findIndex((i) => i.id === room.id);

    if (form.length > 1) {
      form.splice(roomIdIndex, 1);
      formErrors.splice(roomIdIndex, 1);
      if (formErrorsByRoomId?.[room.id]) {
        delete formErrorsByRoomId[room.id];
      }
    }

    // Get the capacity total.
    form.forEach((row) => {
      if (row.title.trim() !== '' && !isNaN(parseInt(row.capacity, 10))) {
        capacityTotal += parseInt(row.capacity, 10);
        roomTotal++;
      }
    });

    handleRoomChanges(form);
    setLocalState({
      capacityTotal,
      form,
      formErrors,
      formErrorsByRoomId,
      isDirty: roomTotal > 0 && capacityTotal > 0,
      roomTotal,
      submitEnabled: roomTotal > 0 && capacityTotal > 0,
    });
  }

  function changeFormState(id: string) {
    return async function (e: React.ChangeEvent<HTMLInputElement>) {
      const index = localState.form.findIndex((i) => i.id === id);
      const name = e.target.name;
      const value = e.target.value;
      let capacityTotal = 0;
      let roomTotal = 0;

      // Deep copy objects in array.
      const form = deepCopy(localState.form);
      const formErrors = deepCopy(localState.formErrors);
      const formErrorsByRoomId = { ...localState.formErrorsByRoomId };
      let submitEnabled = true;

      // Strip out form prefix.
      const simpleFieldName = name.replace(`${UI_PREFIX}-`, '').replace(`-${id}`, '');
      const newObj = {
        [simpleFieldName]: value,
      };

      formErrors[index][simpleFieldName] = '';

      if (formErrorsByRoomId[id]) {
        formErrorsByRoomId[id][simpleFieldName] = '';
      } else {
        formErrorsByRoomId[id] = {
          [simpleFieldName]: '',
        };
      }

      form[index][simpleFieldName] = value;

      // Validate this field if it has any rules.
      if (updateRules[simpleFieldName] !== undefined) {
        await validate(newObj, { [simpleFieldName]: updateRules[simpleFieldName] }, updateMessages).catch((errors) => {
          errors.forEach((err) => {
            formErrors[index][simpleFieldName] = err.message;
            formErrorsByRoomId[id][simpleFieldName] = err.message;
            submitEnabled = false;
          });
        });
      }
      // Get the capacity total.
      form.forEach((row) => {
        if (row.title.trim() !== '' && !isNaN(parseInt(row.capacity, 10))) {
          capacityTotal += parseInt(row.capacity, 10);
          roomTotal++;
        }
      });

      handleDrawerDirty(true);
      handleRoomChanges(form);
      setLocalState({
        capacityTotal,
        form,
        isDirty: capacityTotal > 0,
        formErrors,
        formErrorsByRoomId,
        roomTotal,
        submitEnabled: submitEnabled && roomTotal > 0 && capacityTotal > 0,
      });
    };
  }

  function addNewRooms(finalForm: Room[] = [], formWIds: RoomWId[] = []) {
    const input = {
      source: type,
      rooms: finalForm,
    };
    createRooms({
      variables: { input },
      refetchQueries: ['getSiteStats', 'getOnlyRooms'],
    })
      .then(() => {
        onClose();

        // Confirm rooms added successfully.
        dispatchModal(
          <OneButtonModal
            onClose={() => {
              scrollTo('add-room');
              navigate('/rooms');
            }}
            buttonLabel='Close'
            title={finalForm.length > 1 ? 'You’ve added rooms successfully.' : 'You’ve added a room successfully.'}
            modalId='confirm-room-add-successfully'
            variant='success'
          />
        );
      })
      .catch((errors) => {
        scrollTo(UI_PREFIX, 'add-rooms-side-drawer');
        handleDrawerDirty(true);
        let submitErrors;
        const graphQLErrors = extractGraphQLErrors(errors);
        const networkErrors = extractNetworkErrors(errors);
        if (graphQLErrors) {
          submitErrors = buildValidationErrorMessages(graphQLErrors, UI_PREFIX, updateMessages);
          submitErrors = submitErrors.map((err) => {
            if (err && err.errorMessage === updateMessages['title.unique'] && err.key) {
              const key_fields = err.key.split('-');
              const index = parseInt(key_fields[key_fields.length - 1], 10);
              err.errorMessage = finalForm[index]?.title
                ? `This room: ${finalForm[index]?.title} is already in use.`
                : err.errorMessage;
            }
            //Retarget the key and uiName to point to the correct rooms for submitErrors.
            //Note: uiName is the id responsible for setting up the a-tag to target the input relating to the submitError
            if (err?.key) {
              const key = err.key.split('-');
              // Get the last part of the key and parse it as an integer to get the index
              const index = parseInt(key[key.length - 1], 10);
              // Get the room ID from the formWIds array using the index, default to 0 if not found
              const roomId = formWIds[index].id || 0;
              // Reconstruct the key with the room ID replacing the last part
              err.key = `${key.slice(0, key.length - 1).join('-')}-${roomId}`;
            }

            if (err?.uiName) {
              const key = err.uiName.split('-');
              // Get the last part of the uiName and parse it as an integer to get the index
              const index = parseInt(key[key.length - 1], 10);
              // Get the room ID from the formWIds array using the index, default to 0 if not found
              const roomId = formWIds[index].id || 0;
              // Reconstruct the uiName with the room ID replacing the last part
              err.uiName = `${key.slice(0, key.length - 1).join('-')}-${roomId}`;
            }
            return err;
          });

          setLocalState({ submitErrors });
        } else if (networkErrors) {
          submitErrors = buildNetworkErrorMessages(networkErrors, UI_PREFIX, updateMessages);
          setLocalState({ submitErrors });
        }

        setLocalState({
          isProcessing: false,
          submitEnabled: false,
          submitErrors,
        });
      });
  }

  /**
   *
   * @param form - type of Room w id i.e. { title: string, capacity: string, id: string }
   */
  async function validateTheForm(form) {
    const { capacityTotal } = localState;
    const formErrors: Room[] = [];
    const formErrorsByRoomId: { [key: string]: Room } = {};

    let submitEnabled = true;
    const namesArr = [];

    const catchCallback = (index: number, id: string) => (errors) => {
      submitEnabled = false;
      errors.forEach((err) => {
        formErrors[index][err.field] = err.message;
        formErrorsByRoomId[id][err.field] = err.message;
      });
    };

    for (let index = 0, len = form.length; index < len; index++) {
      // Reset this row's validation first and only set if there's an issue.
      formErrors.push({ ...formFields });
      formErrorsByRoomId[form[index].id] = { ...formFields };
      // If they filled out one or the other field, validate both.
      if (form[index] && (form[index].title || form[index].capacity)) {
        // Push the name for this room to a temporary array to detect duplicates.
        if (namesArr.indexOf(form[index].title.toLowerCase()) === -1) {
          // We don't have this, add it to the array.
          namesArr.push(form[index].title.toLowerCase());
        } else {
          submitEnabled = false;
          formErrors[index]['title'] = 'This room name is already in use.';
          formErrorsByRoomId[form[index].id]['title'] = 'This room name is already in use.';
        }

        // Validate this row.
        await validateAll(form[index], updateRules, updateMessages).catch(catchCallback(index, form[index].id));
      }
    }
    handleRoomChanges(form);

    return {
      form,
      formErrors,
      formErrorsByRoomId,
      isDirty: capacityTotal > 0,
      submitEnabled: !localState.isProcessing && submitEnabled,
    };
  }

  async function processForm() {
    let capacity = '';
    const processedForm: RoomWId[] = [];
    let hasErrors = false;
    let title = '';

    // Clear any empty rows where they didn't fill anything out.
    localState.form.forEach((formRow) => {
      capacity = !isNaN(parseInt(formRow.capacity, 10)) ? parseInt(formRow.capacity, 10) : 0;
      title = formRow.title ? formRow.title.trim() : '';

      if (capacity || title) {
        // Convert Capacity to int.
        processedForm.push({
          id: formRow.id,
          capacity,
          title,
        });
      }
    });

    // Ensure we have at least 1 row.
    if (processedForm.length > 0) {
      // Validate the form one last time.
      // eslint-disable-next-line
      const { form, formErrors, formErrorsByRoomId, isDirty, submitEnabled } = await validateTheForm(processedForm);

      //Remove the id from the final form, since BE will handle assignment of ids
      const finalFormWithoutIds = processedForm.map((room) => {
        return { title: room.title, capacity: room.capacity };
      });

      // If we have any client errors, find the first one and scroll to it.
      for (let formErrIndex = 0, formErrLen = formErrors.length; formErrIndex < formErrLen; formErrIndex++) {
        if (formErrors[formErrIndex]?.title) {
          hasErrors = true;
          scrollTo(`${UI_PREFIX}-title-${formErrIndex}`, 'add-rooms-side-drawer');
          break;
        } else if (formErrors[formErrIndex]?.capacity) {
          hasErrors = true;
          scrollTo(`${UI_PREFIX}-capacity-${formErrIndex}`, 'add-rooms-side-drawer');
          break;
        }
      }

      // Check if we're good to go.
      if (!hasErrors) {
        // Submit the form to GraphQL!
        setLocalState({ form: processedForm });
        addNewRooms(finalFormWithoutIds, processedForm);
      } else {
        setLocalState({
          isProcessing: false,
          form: processedForm,
          formErrors,
          formErrorsByRoomId,
          isDirty,
          submitEnabled,
        });
      }
    }
  }

  function handleSubmit(e) {
    e && e.preventDefault && e.preventDefault();

    // reset drawerDirty to false
    handleDrawerDirty(false);

    if (localState.submitEnabled && !localState.isProcessing) {
      setLocalState({
        isProcessing: true,
        submitEnabled: false,
      });

      processForm();
    }
  }

  const UI_PREFIX = 'bulk-add-room-form';

  // Global App state.
  const dispatchModal = useContext(ModalDispatchContext);

  const formFields = {
    capacity: '',
    title: '',
  };

  useEffect(() => {
    const resetForm = [];
    const resetFormErrors = [];

    const resetFormErrorsByRoomId: { [key: string]: { title: string; capacity: string } } = {};

    if (rooms && rooms.length > 0) {
      for (let i = 0; i < rooms.length; i++) {
        const room = { ...rooms[i], id: uniqueId() };
        resetForm.push(room);
        resetFormErrors.push({ ...formFields });

        initialFormErrorsByRoomId[room.id] = { title: room.title || '', capacity: room.capacity || '' };
      }
      handleDrawerDirty(true);
    } else {
      for (let i = 0; i < initialRows; i++) {
        const room = { ...formFields, id: uniqueId() };
        resetForm.push(room);

        resetFormErrors.push({ ...formFields });
        initialFormErrorsByRoomId[room.id] = { title: room.title || '', capacity: room.capacity || '' };
      }
    }
    setLocalState({
      form: resetForm,
      formErrors: resetFormErrors,
      formErrorsByRoomId: resetFormErrorsByRoomId,
      submitEnabled: rooms.length > 0,
      capacityTotal: rooms.reduce((acc, room) => acc + parseInt(room.capacity, 10), 0),
      roomTotal: rooms.length,
    });
    // eslint-disable-next-line
  }, [rooms]);
  // Prepare the initial form, using the number of initial rows to populate it.
  const initialForm = [];

  const initialFormErrorsByRoomId: { [key: string]: { title: string; capacity: string } } = {};
  const initialFormErrors = [];

  // Populate the initial form with fields that can be filled later.

  if (rooms && rooms.length > 0) {
    for (let i = 0; i < rooms.length; i++) {
      const room = { ...rooms[i], id: uniqueId() };
      initialForm.push(room);
      initialFormErrors.push({ ...formFields });

      initialFormErrorsByRoomId[room.id] = { title: room.title || '', capacity: room.capacity || '' };
    }
  } else {
    for (let i = 0; i < initialRows; i++) {
      const room = { ...formFields, id: uniqueId() };

      initialForm.push(room);
      initialFormErrors.push({ ...formFields });

      initialFormErrorsByRoomId[room.id] = { title: room.title || '', capacity: room.capacity || '' };
    }
  }

  // Local state.
  const [localState, setLocalState] = React.useReducer(localStateReducer, {
    capacityTotal: 0,
    form: initialForm,
    formErrors: initialFormErrors,
    formErrorsByRoomId: initialFormErrorsByRoomId,
    importError: '',
    isDirty: false,
    isProcessing: false,
    roomTotal: 0,
    selectedPastEvent: '',
    submitEnabled: false,
    submitErrors: [],
    pageOpts: { pageNum: 0 },
  });

  useEffect(() => {
    setLocalState({ submitErrors: [] });
  }, [enableResetBtn]);
  // Apollo.
  const [createRooms] = useMutation(CREATE_ROOMS);

  return (
    <form className='cb-form' id={UI_PREFIX}>
      <div className={`row sticky-wrapper m-0 p-2 form-content ${type === 'import' ? 'form-content-import' : ''}`}>
        {!(type === 'import' && rooms.length === 0) && (
          <>
            <div className='col-md-12'>
              {/* Navigate/scroll the view to the errorMessages component and then put focus */}
              <ErrorMessages errorMessages={localState.submitErrors} scrollableWindowId='add-rooms-side-drawer' />

              <p>* = Required</p>
              <RosterV3
                forceDesktopViewForMobile={true}
                headers={headers}
                id={'room-form-table'}
                groupName='addRoomRoster'
                emptyMessage={''}
                items={localState.form}
                striped={false}
                pageNum={localState.pageOpts.pageNum}
                onPageChange={(num: number) => {
                  setLocalState({
                    pageOpts: { pageNum: num },
                  });
                }}
                tableClassName='cb-table cb-input-default table-sticky--no-nav room-form-table'
                // eslint-disable-next-line
                renderItem={(room, options, i) => {
                  return (
                    <AddRoomRow
                      roomRow={room}
                      handleDelete={handleDelete}
                      editRoomHandler={changeFormState}
                      formError={localState.formErrorsByRoomId[room.id] || { title: '', capacity: '' }}
                      UI_PREFIX={UI_PREFIX}
                      deleteEnabled={true}
                      key={`room_${room.id}`}
                    />
                  );
                }}
                stickyHeaders={true}
              />
            </div>
            <div className='display-flex my-4 form-add-row'>
              <PrimaryButton data-automation='button-add-row' onClick={handleAddRow} small>
                <Icon name='plus' size='12' style={{ paddingRight: '5px' }} />
                Add Row
              </PrimaryButton>
            </div>{' '}
          </>
        )}
      </div>
      <div className='display-flex form-footer'>
        <p
          data-automation='import-rooms-total'
          aria-atomic='true'
          aria-live='polite'
          className='cb-roboto-bold py-2 ml-4'
        >
          Total: {localState.capacityTotal} seat{`${localState.capacityTotal === 1 ? '' : 's'}`} in{' '}
          {localState.roomTotal} room{`${localState.roomTotal === 1 ? '' : 's'}`}
        </p>
        <YellowButton
          small
          disabled={!localState.submitEnabled}
          data-automation={'button-add-room-form-confirm'}
          onClick={handleSubmit}
          className='flex--justify-right ml-auto mr-4'
        >
          {footerButtonLabel}
        </YellowButton>
      </div>
    </form>
  );
}

export default BulkAddRooms;
